import type { EarningsDay } from "@/lib/api/earnings";
import { cn } from "@/lib/utils/cn";
import { useAmountPrivacy } from "./useAmountPrivacy";

/** The month day by day: how much each day brought. Counted days are solid,
 * days waiting for attendance are outlined, days still to come are faint, and
 * off days with no pay are a thin tick. It draws only what the server sent. */
export function EarningsTimeline({ days, monthLabel }: { days: EarningsDay[]; monthLabel: string }) {
  const { money } = useAmountPrivacy();
  if (days.length === 0) return null;
  const max = Math.max(...days.map((d) => d.total), 0);
  if (max <= 0) return null;

  const counted = days.filter((d) => d.counted).length;
  const pending = days.filter((d) => d.state === "pending").length;
  const upcoming = days.filter((d) => d.state === "upcoming").length;

  return (
    <div data-testid="earnings-timeline">
      <div className="flex items-baseline justify-between">
        <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Earnings timeline</p>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {counted} counted{pending > 0 ? `, ${pending} pending` : ""}
          {upcoming > 0 ? `, ${upcoming} to come` : ""}
        </p>
      </div>
      <div
        role="img"
        aria-label={`Daily earnings for ${monthLabel}: ${counted} days counted, ${pending} waiting for attendance, ${upcoming} still to come.`}
        className="mt-2 flex h-16 items-end gap-[3px]"
      >
        {days.map((d) => {
          const height = d.total > 0 ? Math.max(8, Math.round((d.total / max) * 100)) : 4;
          return (
            <div
              key={d.date}
              title={`${d.date.slice(8)}: ${money(d.total)}${d.state === "pending" ? " (waiting for attendance)" : d.state === "upcoming" ? " (to come)" : ""}`}
              style={{ height: `${height}%` }}
              className={cn(
                "min-w-[4px] flex-1 rounded-sm transition-[height] duration-500 motion-reduce:transition-none",
                d.counted && d.total > 0 && "bg-emerald-500",
                d.counted && d.total <= 0 && "bg-slate-300 dark:bg-slate-600",
                d.state === "pending" && "border border-dashed border-amber-500 bg-amber-100 dark:border-amber-400 dark:bg-amber-950/60",
                d.state === "upcoming" && "bg-slate-200 dark:bg-slate-700",
              )}
            />
          );
        })}
      </div>
      <div className="mt-1 flex justify-between text-[11px] text-slate-400 tabular-nums" aria-hidden="true">
        <span>{days[0].date.slice(8)}</span>
        <span>{days[days.length - 1].date.slice(8)}</span>
      </div>
    </div>
  );
}
